import {
  ASSET_LIABILITY,
  isPlaidInvestmentAccount,
  selectPlaidDisplayBalance,
} from '@wavvest/shared-utils';

// Allocation classes in display order, with their chart colors.
export const ALLOCATION_CLASSES = [
  { name: 'Equities', color: '#1656BD' },
  { name: 'Commodities', color: '#0A94B0' },
  { name: 'Fixed Income', color: '#14664C' },
  { name: 'Cash', color: '#00483B' },
  { name: 'Other', color: '#6B7280' },
] as const;

export type AllocationClass = (typeof ALLOCATION_CLASSES)[number]['name'];
export type AllocationTotals = Record<AllocationClass, number>;

export const emptyAllocation = (): AllocationTotals => ({
  Equities: 0,
  Commodities: 0,
  'Fixed Income': 0,
  Cash: 0,
  Other: 0,
});

export const isAllocationClass = (value: string): value is AllocationClass =>
  ALLOCATION_CLASSES.some((item) => item.name === value);

// Structural shapes of the Plaid holdings both portals load.
export type PlaidAllocationHolding = { type?: string | null; value?: number | null };
export type PlaidAllocationSecurity = {
  type?: string | null;
  is_cash_equivalent?: boolean | null;
  holdings?: PlaidAllocationHolding[] | null;
};
export type PlaidAllocationHoldings = { securities?: PlaidAllocationSecurity[] | null };
export type PlaidAllocationAccount = Parameters<typeof selectPlaidDisplayBalance>[0] & {
  asset_or_liability?: string | null;
};

/**
 * Allocation class for a Plaid holding. Valid Plaid security types: cash,
 * cryptocurrency, derivative, equity, etf, fixed income, loan, mutual fund,
 * other.
 */
export const classifyPlaidHolding = (
  holdingType?: string | null,
  isCashEquivalent?: boolean | null,
): AllocationClass => {
  // Cash equivalents (e.g. money market funds) count as cash whatever their type.
  if (isCashEquivalent) {
    return 'Cash';
  }

  switch ((holdingType || '').toLowerCase().trim()) {
    case 'cash':
      return 'Cash';
    // Equities, multi-asset ETFs, pooled funds, crypto and derivatives.
    case 'equity':
    case 'etf':
    case 'mutual fund':
    case 'cryptocurrency':
    case 'derivative':
      return 'Equities';
    // Bonds, CDs and loan receivables.
    case 'fixed income':
    case 'loan':
      return 'Fixed Income';
    default:
      return 'Other';
  }
};

/**
 * Allocation of the investor's linked (Plaid) accounts: investment holdings by
 * class, plus depository balances (checking, savings) as cash. Credit and loan
 * accounts are liabilities and are left out.
 */
export const computePlaidAllocation = (
  plaidHoldings: PlaidAllocationHoldings | null | undefined,
  plaidAccounts: PlaidAllocationAccount[] | null | undefined,
): AllocationTotals | null => {
  const securities = Array.isArray(plaidHoldings?.securities) ? plaidHoldings.securities : [];
  const accounts = Array.isArray(plaidAccounts) ? plaidAccounts : [];
  if (securities.length === 0 && accounts.length === 0) {
    return null;
  }

  const allocation = emptyAllocation();

  securities.forEach((security) => {
    (security.holdings ?? []).forEach((holding) => {
      const allocationClass = classifyPlaidHolding(
        holding.type || security.type,
        security.is_cash_equivalent,
      );
      const value = holding.value || 0;
      if (value > 0) {
        allocation[allocationClass] += value;
      }
    });
  });

  const depositoryCash = accounts
    .filter(
      (account) =>
        account.asset_or_liability === ASSET_LIABILITY.ASSET && !isPlaidInvestmentAccount(account),
    )
    .reduce((sum, account) => {
      const balance = selectPlaidDisplayBalance(account);
      return sum + (typeof balance === 'number' ? balance : 0);
    }, 0);
  if (depositoryCash > 0) {
    allocation.Cash += depositoryCash;
  }

  return allocation;
};
